import { uniqueElementsBy } from 'modules/analytics/Utils';
import { AttendanceFeatures } from '../AttendanceFeatures';
export class HistoryService {
    static $inject = ['MessagingHubService', 'MESSAGINGHUBDOMAIN'];
    constructor(MessagingHubService, MESSAGINGHUBDOMAIN) {
        this.MessagingHubService = MessagingHubService;
        this.MESSAGINGHUBDOMAIN = MESSAGINGHUBDOMAIN;
    }
    /**
     * Get application tickets
     * @param filters
     * @param skip
     * @param take
     */
    async get(filters, skip, take, useV2 = false) {
        const filter = filters ? `$filter=${encodeURIComponent(filters)}&` : '';
        let uri = `/tickets/history?${filter}$skip=${skip}&$take=${take}`;
        if (useV2) {
            uri = `/tickets/history/v2?${filters}&$skip=${skip}&$take=${take}`;
        }
        return await this.MessagingHubService.sendCommand({
            method: 'get',
            to: `postmaster@desk.${this.MESSAGINGHUBDOMAIN}`,
            uri,
        });
    }
    /**
     * Get application tickets by customer (with merge)
     * @param filters
     * @param skip
     * @param take
     */
    async getByCustomerMerged(customerIdentity, skip, take) {
        return await this.MessagingHubService.sendCommand({
            method: 'get',
            to: `postmaster@desk.${this.MESSAGINGHUBDOMAIN}`,
            uri: `/tickets/history-merged/${encodeURIComponent(customerIdentity)}?$skip=${skip}&$take=${take}`,
        });
    }
    async getThreads(identity, messageId, skipDate, take = 100, ownerIdentity = undefined, getFromOriginator) {
        const messageIdQueryString = messageId ? `&messageId=${encodeURIComponent(messageId)}` : '';
        const storageDateQueryString = skipDate ? `&storageDate=${skipDate}` : '';
        const getFromOriginatorQuery = getFromOriginator ? '&getFromOriginator=true' : '';
        const identityString = encodeURIComponent(identity);
        let uri = '';
        if (ownerIdentity && !getFromOriginator) {
            uri += `lime://${ownerIdentity}`;
        }
        const [isGetThreadMessageMergedEnabled, useThreadsDomain] = await Promise.all([
            AttendanceFeatures.isGetThreadMessageMergedEnabled(),
            AttendanceFeatures.isThreadsDomainEnabled()
        ]);
        const uriBase = isGetThreadMessageMergedEnabled ? 'threads-merged' : 'threads';
        uri += `/${uriBase}/${identityString}?$take=${take}&direction=desc&refreshExpiredMedia=true`
            + messageIdQueryString + storageDateQueryString + getFromOriginatorQuery;
        const command = {
            method: 'get',
            uri,
        };
        if (useThreadsDomain) {
            command.to = `postmaster@threads.${this.MESSAGINGHUBDOMAIN}`;
        }
        const resource = await this.MessagingHubService.sendCommand(command);
        let items = [];
        if (resource && resource.items && resource.items.length > 0) {
            const doItemsHaveSameId = (item1, item2) => {
                return item1.id === item2.id;
            };
            items = uniqueElementsBy(resource.items, doItemsHaveSameId);
        }
        return {
            ...resource,
            items,
        };
    }
    async getThreadMessage(ownerIdentity, identity, messageId, getFromOriginator) {
        try {
            let uriBase = '';
            if (ownerIdentity && !getFromOriginator) {
                uriBase = `lime://${ownerIdentity}`;
            }
            const getFromOriginatorQuery = getFromOriginator ? '?getFromOriginator=true' : '';
            const resource = await this.MessagingHubService.sendCommand({
                method: 'get',
                uri: `${uriBase}/thread-message/${identity}/${encodeURIComponent(messageId)}${getFromOriginatorQuery}`,
            });
            return resource;
        }
        catch (err) {
            return undefined;
        }
    }
    async sendByEmail(email, filters) {
        await this.MessagingHubService.sendCommand({
            method: 'set',
            to: `postmaster@desk.${this.MESSAGINGHUBDOMAIN}`,
            uri: '/attendance-history/send-by-email',
            type: 'application/json',
            resource: {
                email: encodeURIComponent(email),
                filter: encodeURIComponent(filters),
            },
        });
    }
    /**
     * Send email with archived conversations
     * @param email
     * @param ticketSeqId
     * @param teams
     * @param attendants
     * @param tags
     * @param startTime
     * @param endTime
     * @param customers
     */
    async sendArchivedByEmail(filters) {
        return this.MessagingHubService.sendCommand({
            method: 'set',
            to: `postmaster@desk.${this.MESSAGINGHUBDOMAIN}`,
            uri: '/thread-history/archived/send-by-email',
            type: 'application/vnd.iris.desk.ticket-history-request+json',
            resource: {
                email: encodeURIComponent(filters.email),
                TicketSeqId: filters.ticketSeqId,
                Teams: filters.teams,
                Attendants: filters.attendants,
                Tags: filters.tags,
                StartTime: filters.startTime,
                EndTime: filters.endTime,
                Customers: filters.customers,
            },
        });
    }
    /**
     * Send email with listed tickets
     * @param email
     * @param ticketSeqId
     * @param teams
     * @param attendants
     * @param tags
     * @param startTime
     * @param endTime
     * @param customers
     */
    async sendTicketListArchivedByEmail(filters) {
        return this.MessagingHubService.sendCommand({
            method: 'set',
            to: `postmaster@desk.${this.MESSAGINGHUBDOMAIN}`,
            uri: '/attendance-history/archived/send-by-email',
            type: 'application/vnd.iris.desk.ticket-history-request+json',
            resource: {
                email: encodeURIComponent(filters.email),
                TicketSeqId: filters.ticketSeqId,
                Teams: filters.teams,
                Attendants: filters.attendants,
                Tags: filters.tags,
                StartTime: filters.startTime,
                EndTime: filters.endTime,
                Customers: filters.customers,
            },
        });
    }
    /**
     * Get Tunnel directory account
     * @param nameOrIdentity
    */
    getTunnelAccount(nameOrIdentity) {
        const uri = `/accounts/${encodeURIComponent(nameOrIdentity)}`;
        return this.MessagingHubService.sendCommand({
            method: 'get',
            uri: uri,
            to: 'postmaster@tunnel.msging.net',
        });
    }
    /**
     * Get application active tags
     * @param includeAttendanceQueueTags
     */
    async getTags(includeAttendanceQueueTags = false) {
        return await this.MessagingHubService.sendCommand({
            method: 'get',
            to: `postmaster@desk.${this.MESSAGINGHUBDOMAIN}`,
            uri: `/tags/active?includeAttendanceQueueTags=${includeAttendanceQueueTags}`,
        });
    }
}
HistoryService.$inject = ['MessagingHubService', 'MESSAGINGHUBDOMAIN'];
